import type { ElementRef, OnDestroy } from '@angular/core';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';

import { takeUntil } from 'rxjs/operators';

import { fileToBase64 } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';

import { ButtonComponent } from '../../../common/button/components/button/button.component';
import { IconButtonComponent } from '../../../common/button/components/icon-button/icon-button.component';
import { ButtonLoadingDirective } from '../../../common/button/directives/button-loading/button-loading.directive';
import { DropdownItemComponent } from '../../../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent, DropdownWidth } from '../../../common/dropdown/dropdown.component';
import { HeadingComponent } from '../../../common/heading/heading.component';
import { IconComponent } from '../../../common/icon/icon.component';
import { TruncateComponent } from '../../../common/truncate/truncate.component';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../../core/overlay/overlay-directives';
import { RtInput } from '../../../utils/rt-input';
import { AttachmentItemComponent } from '../attachment-item/attachment-item.component';

@Component({
  selector: 'rt-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.less'],
  imports: [
    AttachmentItemComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    TranslatePipe,
    TruncateComponent,
  ],
})
export class FileUploadComponent
  extends RtInput<File | string | null>
  implements OnDestroy, ControlValueAccessor
{
  private readonly imgTagAllowedExtensions: string[] = ['gif', 'jpg', 'jpeg', 'png', 'svg'];

  @Input() public headingText: string;
  @Input() public buttonText: string;
  @Input() public accept: string;
  @Input() public withIcon: boolean = true;
  @Input() public extensions: string[] | string;
  @Input() public btnVariants: string;
  @Input() public noImage: boolean = false;
  @Input() public hasPreview: boolean = true;
  @Input() public hasOnlyButton: boolean = false;
  @Input() public buttonPrimary: boolean = false;
  @Input() public buttonPaddingSmall: boolean = false;
  @Input() public loadingButton: boolean = false;
  @Input() public downloadable: boolean = true;
  @Input() public base64ModeEnabled: boolean = false;
  @Input() public canManageCandidates: boolean = false;
  @Input() public canDropFile: boolean = false;
  @Input() public extensionsText: string;

  @Output() public extensionError: EventEmitter<boolean> = new EventEmitter();

  public attachmentLike: any;
  public dropAreaVisible: boolean;
  public moreDropdownOpened: boolean;
  public DropdownWidth = DropdownWidth;

  @ViewChild('fileInput', { static: true })
  private fileInput: ElementRef<HTMLInputElement>;
  private fileExtension: string;

  public writeValue(value: File | string | undefined): void {
    if (!value) {
      this.clearValue();
    }
    if (value && typeof value === 'string') {
      this.fileExtension = this.getExtension(value);
      this.updatePreview(value);
    }

    if (this.base64ModeEnabled && value instanceof File) {
      fileToBase64(value)
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe(base64 => {
          super.writeValue(base64);
          this.updatePreview(base64);
          this.cdRef.detectChanges();
        });
    } else {
      super.writeValue(value);
      this.cdRef.detectChanges();
    }
  }

  public onFileChange(files: FileList | null): void {
    if (!files) return;

    const file = files[0];
    if (!file) return;

    const fileName = file.name;
    this.fileExtension = this.getExtension(fileName);
    if (this.extensions && !this.extensions.includes(this.fileExtension)) {
      this.extensionError.emit(true);
      this.clearValue();
      return;
    }

    this.extensionError.emit(false);

    if (!this.base64ModeEnabled) {
      this.handleChange(file);
    }

    fileToBase64(file)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(base64 => {
        this.updatePreview(base64, fileName);
        this.cdRef.detectChanges();

        if (this.base64ModeEnabled) {
          this.handleChange(base64);
        }
      });
  }

  public onDragOver($event: DragEvent): void {
    this.preventAndStop($event);
  }

  public onDrop($event: DragEvent): void {
    this.preventAndStop($event);
    this.switchDropArea(false);

    const files = $event.dataTransfer?.files;

    if (!files) return;
    this.onFileChange(files);
  }

  public switchDropArea(param: boolean): void {
    this.dropAreaVisible = param;
    this.cdRef.detectChanges();
  }

  public switchDropdown(param: boolean): void {
    this.moreDropdownOpened = param;
    this.cdRef.detectChanges();
  }

  public clearValue(): void {
    this.attachmentLike = null;
    this.fileInput.nativeElement.value = <string>(<unknown>null);
    this.handleChange(null);
    this.cdRef.detectChanges();
  }

  private getExtension(fileName: string): string {
    const splitStr = fileName.split('.');
    if (splitStr.length >= 1) {
      return fileName.split('.').pop()!;
    }

    return '';
  }

  private updatePreview(url: string, fileName?: string): void {
    this.attachmentLike = {
      fileUrl: url,
      filename: fileName,
      extension: this.fileExtension,
      imageUrl:
        this.hasPreview && this.imgTagAllowedExtensions.includes(this.fileExtension) ? url : null,
      imageThumbnailUrl:
        this.hasPreview && this.imgTagAllowedExtensions.includes(this.fileExtension) ? url : null,
    };
  }

  private preventAndStop($event: DragEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
  }
}
